// charge visibility rules:
//   shipper / receiver → REVENUE rows (priority); fall back to COST when no REVENUE present. Never both.
//   carrier            → COST rows only (what they owe / get paid). Never REVENUE.
//
// Never surface the DB totalCharge column (mixed sum) in the UI — always derive from charges.

export const FSC_CODE = "FSC";

const CARRIER = "carrier";
const COST_TYPES = ["COST", "COST_PER_STOP"];

function revenueRows(charges) {
  return charges.filter((c) => (c.type || c.chargeType || "").toUpperCase() === "REVENUE");
}

function costRows(charges) {
  return charges.filter((c) =>
    COST_TYPES.includes((c.type || c.chargeType || "").toUpperCase())
  );
}

/**
 * Filter and normalize charges for the viewer's role.
 * Carrier: COST only. Shipper/receiver: REVENUE first, COST as fallback. Never both.
 */
export function filterChargesForRole(charges, role) {
  if (!Array.isArray(charges)) return [];
  if ((role || "").toLowerCase() === CARRIER) {
    return costRows(charges).map((c) => ({ ...c, _typeHidden: true }));
  }
  // shipper / receiver: REVENUE first, COST fallback, never both
  const rev = revenueRows(charges);
  return rev.length > 0 ? rev : costRows(charges);
}

/**
 * Compute charge totals from a charges array.
 * Carrier:          { total } — COST sum
 * Shipper/Receiver: { revenue, cost, profit } using REVENUE rows (COST fallback when no REVENUE)
 */
export function totalChargeForRole(charges, role) {
  if (!Array.isArray(charges) || charges.length === 0) {
    return (role || "").toLowerCase() === CARRIER
      ? { total: 0 }
      : { revenue: 0, cost: 0, profit: 0 };
  }
  if ((role || "").toLowerCase() === CARRIER) {
    const total = costRows(charges).reduce((sum, c) => sum + Number(c.amount ?? 0), 0);
    return { total };
  }
  // shipper / receiver
  const rev = revenueRows(charges).reduce((sum, c) => sum + Number(c.amount ?? 0), 0);
  const cost = costRows(charges).reduce((sum, c) => sum + Number(c.amount ?? 0), 0);
  const revenue = rev > 0 ? rev : cost;
  return { revenue, cost, profit: rev > 0 && cost > 0 ? rev - cost : 0 };
}

/**
 * For list-view context where only pre-aggregated DTO totals are available (no charges array).
 * Returns the same shape as totalChargeForRole.
 */
export function totalChargeFromDTO(revenueTotalCharge, costTotalCharge, role) {
  const revenue = Number(revenueTotalCharge ?? 0);
  const cost = Number(costTotalCharge ?? 0);
  if ((role || "").toLowerCase() === CARRIER) {
    return { total: cost > 0 ? cost : null };
  }
  return {
    revenue: revenue > 0 ? revenue : null,
    cost: cost > 0 ? cost : null,
    profit: revenue > 0 && cost > 0 ? revenue - cost : 0,
  };
}
